import React, { useEffect, useRef, useState } from 'react';
import { regenApi } from '../lib/api';
import { cached } from '../lib/cache';

// One media renderer for the whole studio. For VIDEO it shows a STABLE server-generated JPG
// poster (ffmpeg frame cached in S3, keyed by the source) → never a black card, no refresh churn
// (the poster URL is memoized per source in the client cache). Click to play the actual video.
// Images render object-contain; audio gets an inline player.
export function MediaThumb({ url, s3key, mediaType, className }:
  { url?: string; s3key?: string; mediaType?: string; className?: string }) {
  const key = (s3key || url || '').toLowerCase();
  const isImage = mediaType === 'image' || /\.(jpg|jpeg|png|webp|gif)(\?|#|$)/i.test(key);
  const isAudio = mediaType === 'audio' || /\.(mp3|wav|m4a|aac)(\?|#|$)/i.test(key);
  const [poster, setPoster] = useState<string>('');
  const [playing, setPlaying] = useState(false);
  // Guards the one-shot play() below so it fires once per click, never again on re-render.
  const startedRef = useRef(false);

  useEffect(() => {
    if (!url || isImage || isAudio) return;
    let alive = true;
    // stable hash key = the object PATH (drop the expiring presign query) so the poster is
    // generated once and its URL never churns; fetch uses the full (signed) url.
    const stable = s3key || url.split('?')[0].split('#')[0];
    cached(`thumb:${stable}`, 6 * 3600 * 1000, () => regenApi.thumb({ key: stable, url }))
      .then((r: any) => { if (alive) setPoster(r?.data?.url || ''); })
      .catch(() => {});
    return () => { alive = false; };
  }, [url, s3key, isImage, isAudio]);

  if (!url) return <div className={`${className} bg-black/10 flex items-center justify-center text-[11px] text-gray-400`}>no preview</div>;

  if (isImage) {
    const cls = (className || '').replace(/object-cover/g, 'object-contain');
    return <img src={url} alt="" className={cls} loading="lazy" />;
  }
  if (isAudio) {
    return (
      <div className={`${className} bg-gradient-to-br from-indigo-500/20 to-fuchsia-500/20 flex flex-col items-center justify-center gap-2 p-3`}>
        <span className="text-3xl">🎙️</span>
        <audio src={url} controls preload="none" className="w-full" />
      </div>
    );
  }
  // video — poster until clicked. We NEVER autoplay: no autoPlay attribute, no mount/scroll play().
  // The <video> only mounts as a direct result of the user clicking the ▶ overlay, and we start it
  // from THAT click via the ref — so playback is always the user's explicit action, never automatic.
  if (playing) {
    return (
      <video
        ref={(el) => { if (el && !startedRef.current) { startedRef.current = true; el.play().catch(() => {}); } }}
        src={url} controls playsInline preload="metadata"
        className={className} poster={poster || undefined}
      />
    );
  }
  return (
    <button type="button" onClick={() => setPlaying(true)} className={`${className} relative group p-0 border-0 bg-black overflow-hidden`}>
      {poster
        ? <img src={poster} alt="" className={(className || '').replace(/object-cover/g, 'object-cover')} loading="lazy" />
        : <div className="w-full h-full flex items-center justify-center bg-slate-800 text-slate-400 text-[11px] animate-pulse">loading…</div>}
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="w-9 h-9 rounded-full bg-black/55 group-hover:bg-black/75 flex items-center justify-center text-white text-sm">▶</span>
      </span>
    </button>
  );
}

export default MediaThumb;
